import { useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Badge } from "../../../components/ui/badge";

export function ModelSelector({ models = [], value, onChange, placeholder = "Select model...", loading = false, disabled = false }) {
  const [open, setOpen] = useState(false);

  const selectedModel = models.find(m => m.id === value);
  const displayName = selectedModel ? (selectedModel.name || selectedModel.id) : value || "";

  // Group models
  const freeModels = models.filter(m => m.id.includes(":free"));
  const openaiModels = models.filter(m => m.id.startsWith("openai/") && !m.id.includes(":free"));
  const anthropicModels = models.filter(m => m.id.startsWith("anthropic/"));
  const googleModels = models.filter(m => m.id.startsWith("google/") && !m.id.includes(":free"));
  const otherModels = models.filter(m =>
    !m.id.startsWith("openai/") &&
    !m.id.startsWith("anthropic/") &&
    !m.id.startsWith("google/") &&
    !m.id.includes(":free")
  );

  const groups = [
    { label: "Free Models", items: freeModels, badge: "Free", badgeClass: "bg-green-100 text-green-700" },
    { label: "OpenAI", items: openaiModels },
    { label: "Anthropic", items: anthropicModels },
    { label: "Google", items: googleModels },
    { label: "Other Models", items: otherModels },
  ].filter(g => g.items.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-10 text-sm"
          disabled={disabled || loading}
          data-testid="model-selector-trigger"
        >
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />Loading models...
            </span>
          ) : displayName ? (
            <span className="truncate">{displayName}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search models... (e.g. gpt-4o, claude, gemini, free)" />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No models found.</CommandEmpty>
            {groups.map(group => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.items.map(model => (
                  <CommandItem
                    key={model.id}
                    value={`${model.id} ${model.name || ""}`}
                    onSelect={() => {
                      onChange(model.id);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Check className={cn("h-3 w-3 shrink-0", value === model.id ? "opacity-100" : "opacity-0")} />
                      <span className="truncate text-sm">{model.name || model.id}</span>
                    </div>
                    {group.badge && (
                      <Badge variant="secondary" className={cn("text-xs shrink-0 ml-2", group.badgeClass)}>
                        {group.badge}
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
